import React, { useCallback } from 'react';
import classNames from 'classnames';
import { RuleStepEvaluation } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/DebugData/RuleStepEvaluation';
import { ListStepEvaluationItem } from './ListStepEvaluationItem';
import { RuleExecutionResult } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/Events';
import { FullListStepEvaluation } from '.';
import './ListStepEvaluation.less';

export type SeeAllClickEventHandler = (event: { target: HTMLElement; evaluations: RuleStepEvaluation[] }) => void;

export interface IListStepEvaluationProps extends React.ComponentProps<'div'> {
  /**
   * Indicates how much inline results can be shown
   *
   * @default 8
   */
  maxEvaluationsNumber?: number;
  /**
   * Evaluation results
   */
  evaluations: RuleStepEvaluation[];
  /**
   * "See All" click button handler.
   * When amount of evaliations more then maximum "See All" button will be shown.
   */
  onSeeAllClick: SeeAllClickEventHandler;
}

const getClassName = (className?: string) => classNames('dpc-list-step-evaluation', className);

const defaultMaxEvaluationsNumber = 8;

/**
 * Shows list step evaluation result inline for sampling/unit tests/debugger
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ListStepEvaluation({
  className,
  maxEvaluationsNumber = defaultMaxEvaluationsNumber,
  evaluations,
  onSeeAllClick,
  ...otherProps
}: IListStepEvaluationProps) {
  const areAllVisible = maxEvaluationsNumber > evaluations.length;

  return (
    <div className={getClassName(className)} {...otherProps}>
      {areAllVisible && <FullListStepEvaluation evaluations={evaluations} />}

      {!areAllVisible && (
        <ShortedEvaluationView
          maxEvaluationsNumber={maxEvaluationsNumber}
          evaluations={evaluations}
          onSeeAllClick={onSeeAllClick}
        />
      )}
    </div>
  );
}

function ShortedEvaluationView({
  maxEvaluationsNumber = defaultMaxEvaluationsNumber,
  evaluations,
  onSeeAllClick,
}: IListStepEvaluationProps) {
  const lastVisibleItem = maxEvaluationsNumber - 1;
  const visibleEvaluations = evaluations.slice(0, lastVisibleItem);
  const remainigEvaluations = evaluations.slice(maxEvaluationsNumber - 1, evaluations.length);
  //if out of max shown range, show the first success one or the last evaluation
  const successRemainingIndex = remainigEvaluations.findIndex(
    (item, index) => item.Result === RuleExecutionResult.Success || index === remainigEvaluations.length - 1
  );

  const handleSeeAllClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onSeeAllClick && onSeeAllClick({ evaluations, target: event.target as HTMLElement });
    },
    [evaluations, onSeeAllClick]
  );

  return (
    <>
      <ol>
        {visibleEvaluations.map((item, index) => (
          <ListStepEvaluationItem key={`${item.Anchor} ${item.Verb} ${index}`} evaluation={item} index={index + 1} />
        ))}
        <ListStepEvaluationItem
          hasSkippedAbove={successRemainingIndex !== 0}
          hasSkippedBelow={successRemainingIndex !== remainigEvaluations.length - 1}
          evaluation={remainigEvaluations[successRemainingIndex]}
          index={visibleEvaluations.length + successRemainingIndex + 1}
        />
      </ol>
      <button className="dpc-list-step-evaluation__show-all link" onClick={handleSeeAllClick}>
        (See All - {evaluations.length})
      </button>
    </>
  );
}
